import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  BookOpen,
  Boxes,
  Cable,
  Command,
  Activity,
  Loader2,
  MessageSquareText,
  ShieldCheck,
  Sparkles,
  Workflow,
  Wrench,
} from 'lucide-react';
import { AI_MODEL_OPTIONS } from '@oneup/shared';
import {
  ALICE_COMPOSER_WORKFLOWS,
  ALICE_SUGGESTION_CATALOG,
  ALICE_TOOL_CATEGORY_LABELS,
  type AliceConnectionStatus,
  type AliceEffectivePolicy,
  type AliceToolCategory,
  type AliceToolInfo,
  type AliceWorkflowStatus,
} from '@command-center/core-platform';
import { aiLogsApi, copilotApi, type AiLogRow } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useAiSettingsStore } from '@/stores/ai-settings';
import { useUiStore } from '@/stores/ui';
import { cn } from '@/lib/utils';

/**
 * Alice Framework — read-only visibility into Alice's tools, connections,
 * prompts, models, and workflows. The catalog of tools/connections/prompts is
 * the Worker's source of truth (GET /api/copilot/registry, derived from the
 * actual tool definitions); models/workflows/suggestions are the shared
 * frontend catalogs. Add new tools to the registry, not ad hoc, to keep this
 * page honest.
 */
export function AliceFrameworkView() {
  const registry = useQuery({
    queryKey: ['copilot', 'registry'],
    queryFn: () => copilotApi.registry(),
    staleTime: 60_000,
  });
  const backstage = useUiStore((state) => state.backstage);
  const [showHidden, setShowHidden] = useState(backstage);
  // Unlocking backstage reveals hidden workflows; locking it hides them again.
  useEffect(() => {
    setShowHidden(backstage);
  }, [backstage]);
  const disabledConnectors = useAiSettingsStore((state) => state.disabledConnectors);
  const toggleConnector = useAiSettingsStore((state) => state.toggleConnector);

  const connections = registry.data?.connections ?? [];
  const configuredCount = connections.filter((c) => c.status === 'configured').length;
  const allWorkflows = registry.data?.workflows ?? [];
  const allAliases = registry.data?.commandAliases ?? [];
  const hiddenCount = allWorkflows.filter((w) => w.visibility === 'private_hidden').length;
  const workflows = showHidden ? allWorkflows : allWorkflows.filter((w) => w.visibility !== 'private_hidden');
  const aliases = showHidden ? allAliases : allAliases.filter((a) => a.visibility !== 'private_hidden');

  const toolsByCategory = useMemo(() => {
    const groups = new Map<AliceToolCategory, AliceToolInfo[]>();
    for (const tool of registry.data?.tools ?? []) {
      if (tool.category === 'vault') continue;
      const list = groups.get(tool.category) ?? [];
      list.push(tool);
      groups.set(tool.category, list);
    }
    return Array.from(groups.entries()).sort((a, b) =>
      ALICE_TOOL_CATEGORY_LABELS[a[0]].localeCompare(ALICE_TOOL_CATEGORY_LABELS[b[0]]),
    );
  }, [registry.data]);

  const vaultTools = useMemo(
    () => (registry.data?.tools ?? []).filter((tool) => tool.category === 'vault'),
    [registry.data],
  );
  const toolCount = registry.data?.tools.length ?? 0;
  const writeCount = registry.data?.tools.filter((t) => t.access === 'write').length ?? 0;

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-[22px] font-semibold leading-tight">
            <Sparkles className="h-5 w-5 text-primary" aria-hidden />
            Alice Framework
          </h1>
          <p className="text-[13px] text-muted-foreground">
            A live catalog of what Alice can do — tools, skills/workflows, command aliases,
            connections, prompts, models. Generated from the Worker so it never drifts.
          </p>
        </div>
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setShowHidden((v) => !v)}
            className="rounded-md border border-border px-2.5 py-1 text-[12px] text-muted-foreground transition-colors hover:text-foreground"
          >
            {showHidden ? 'Hide private' : `Show hidden (${hiddenCount})`}
          </button>
        )}
      </header>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Tools" value={toolCount} />
        <Stat label="Skills / Workflows" value={allWorkflows.length} />
        <Stat label="Connections" value={connections.length ? `${configuredCount}/${connections.length}` : '—'} hint="configured" />
        <Stat label="Models" value={AI_MODEL_OPTIONS.length} />
      </div>

      {registry.isLoading ? (
        <Card className="flex items-center gap-2 px-4 py-6 text-[13px] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading registry…
        </Card>
      ) : registry.error ? (
        <Card className="flex items-start gap-2 px-4 py-4 text-[13px] text-destructive">
          <AlertCircle className="h-4 w-4" aria-hidden />
          Couldn't load the registry.{' '}
          {registry.error instanceof Error ? registry.error.message : 'Unknown error'}
        </Card>
      ) : (
        <>
          {/* Effective Policy (server-enforced, read-only) */}
          {registry.data?.effectivePolicy && (
            <EffectivePolicySection policy={registry.data.effectivePolicy} />
          )}

          {/* Recent Alice Activity (audit, from ai_logs) */}
          <RecentAliceActivity />

          {/* Vault Tools */}
          {vaultTools.length > 0 && (
            <Section
              icon={BookOpen}
              title="Vault Tools"
              subtitle="Gateway-backed .MD document search, read, and approval proposals"
            >
              <div className="grid gap-2">
                {vaultTools.map((tool) => (
                  <VaultToolCard key={tool.name} tool={tool} />
                ))}
              </div>
            </Section>
          )}

          {/* Tools */}
          <Section
            icon={Wrench}
            title="Tools"
            subtitle={`${toolCount} tools · ${writeCount} write (approval-gated) · ${toolCount - writeCount} read`}
          >
            <div className="space-y-4">
              {toolsByCategory.map(([category, tools]) => (
                <div key={category}>
                  <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                    {ALICE_TOOL_CATEGORY_LABELS[category]}
                  </h3>
                  <div className="overflow-hidden rounded-lg border border-border">
                    {tools.map((tool, idx) => {
                      const toolOff = disabledConnectors.includes(tool.connectorId);
                      return (
                      <div
                        key={tool.name}
                        className={cn(
                          'flex flex-col gap-1 px-3 py-2.5 sm:flex-row sm:items-center sm:gap-3',
                          idx > 0 && 'border-t border-border',
                          toolOff && 'opacity-50',
                        )}
                      >
                        <div className="flex items-center gap-2 sm:w-56 sm:flex-shrink-0">
                          <code className="font-mono text-[12px] text-foreground">{tool.name}</code>
                          <Badge variant={tool.access === 'write' ? 'warning' : 'muted'} className="h-5 px-1.5 text-[10px]">
                            {tool.access}
                          </Badge>
                          {tool.requiresApproval && (
                            <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
                              approval
                            </Badge>
                          )}
                          {toolOff && (
                            <Badge variant="warning" className="h-5 px-1.5 text-[10px]">
                              off
                            </Badge>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] leading-snug text-muted-foreground">{tool.description}</p>
                          <p className="mt-0.5 text-[11px] text-muted-foreground/70">{tool.dataSource}</p>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Skills / Workflows */}
          <Section
            icon={Workflow}
            title="Skills / Workflows"
            subtitle={`${workflows.length} reusable multi-step behaviors`}
          >
            <div className="space-y-2">
              {workflows.map((wf) => (
                <div key={wf.workflowKey} className="rounded-lg border border-border px-3 py-2.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[13px] font-medium text-foreground">{wf.displayName}</span>
                    <code className="font-mono text-[11px] text-muted-foreground">{wf.workflowKey}</code>
                    <WorkflowStatusBadge status={wf.status} />
                    <Badge variant="muted" className="h-5 px-1.5 text-[10px]">Risk {wf.riskLevel}</Badge>
                    <Badge variant="outline" className="h-5 px-1.5 text-[10px]">approval: {wf.approvalMode}</Badge>
                    <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{wf.visibility}</Badge>
                    {!wf.enabled && (
                      <Badge variant="warning" className="h-5 px-1.5 text-[10px]">disabled</Badge>
                    )}
                  </div>
                  <p className="mt-1 text-[12px] text-muted-foreground">{wf.description}</p>
                  {(wf.triggerAliases.length > 0 || wf.voiceTriggers.length > 0) && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {wf.triggerAliases.map((a) => (
                        <code key={a} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                          {a}
                        </code>
                      ))}
                      {wf.voiceTriggers.map((a) => (
                        <code key={a} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                          “{a}”
                        </code>
                      ))}
                    </div>
                  )}
                  {wf.toolsUsed.length > 0 && (
                    <p className="mt-1 text-[11px] text-muted-foreground/70">Tools: {wf.toolsUsed.join(', ')}</p>
                  )}
                </div>
              ))}
            </div>
          </Section>

          {/* Command Aliases */}
          <Section icon={Command} title="Command Aliases" subtitle={`${aliases.length} triggers`}>
            <div className="overflow-hidden rounded-lg border border-border">
              {aliases.map((alias, idx) => {
                const target = allWorkflows.find((w) => w.workflowKey === alias.targetKey);
                return (
                  <div
                    key={alias.aliasKey}
                    className={cn(
                      'flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-[12px]',
                      idx > 0 && 'border-t border-border',
                    )}
                  >
                    <code className="font-mono text-foreground sm:w-48">{alias.triggerText}</code>
                    <span className="min-w-0 flex-1 truncate text-muted-foreground">
                      → {target?.displayName ?? alias.targetKey}
                    </span>
                    <Badge variant="muted" className="h-5 px-1.5 text-[10px]">{alias.triggerType}</Badge>
                    {target && (
                      <Badge variant="muted" className="h-5 px-1.5 text-[10px]">Risk {target.riskLevel}</Badge>
                    )}
                    {alias.requiresConfirmation && (
                      <Badge variant="outline" className="h-5 px-1.5 text-[10px]">confirm</Badge>
                    )}
                    <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{alias.visibility}</Badge>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* Connections */}
          <Section
            icon={Cable}
            title="Connections"
            subtitle="External systems Alice's tools rely on · switch optional ones on/off"
          >
            <div className="grid gap-2 sm:grid-cols-2">
              {connections.map((conn) => {
                const off = disabledConnectors.includes(conn.id);
                return (
                  <div
                    key={conn.id}
                    className={cn(
                      'flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 transition-opacity',
                      off && 'opacity-60',
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-foreground">{conn.label}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {conn.kind}
                        {conn.detail ? ` · ${conn.detail}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <ConnectionBadge status={conn.status} />
                      {conn.toggleable && (
                        <Switch
                          checked={!off}
                          onCheckedChange={() => toggleConnector(conn.id)}
                          aria-label={`Toggle ${conn.label}`}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Toggles are saved on this device and tell Alice to withhold that connector's tools.
              Core connections (Airtable records, OpenAI) can't be switched off.
            </p>
          </Section>

          {/* Prompts */}
          <Section icon={MessageSquareText} title="Prompts" subtitle="What Alice is told on every turn">
            <div className="space-y-2">
              {(registry.data?.prompts ?? []).map((prompt) => (
                <div key={prompt.id} className="rounded-lg border border-border px-3 py-2.5">
                  <p className="text-[13px] font-medium text-foreground">{prompt.label}</p>
                  <p className="mt-0.5 text-[12px] text-muted-foreground">{prompt.description}</p>
                  {prompt.preview && (
                    <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-muted/50 px-2.5 py-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
                      {prompt.preview}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </Section>
        </>
      )}

      {/* Models (frontend catalog) */}
      <Section icon={Sparkles} title="Models" subtitle="OpenAI models Alice can use (selectable in Settings)">
        <div className="grid gap-2 sm:grid-cols-2">
          {AI_MODEL_OPTIONS.map((model) => (
            <div key={model.id} className="rounded-lg border border-border px-3 py-2.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[13px] font-medium text-foreground">{model.label}</p>
                <code className="font-mono text-[11px] text-muted-foreground">{model.model}</code>
              </div>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{model.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Workflows (frontend catalog) */}
      <Section icon={Workflow} title="Composer workflows" subtitle="Guarded draft sessions Alice can run">
        <div className="grid gap-2 sm:grid-cols-2">
          {ALICE_COMPOSER_WORKFLOWS.map((wf) => (
            <div key={wf.targetType} className="rounded-lg border border-border px-3 py-2.5">
              <p className="text-[13px] font-medium text-foreground">{wf.label}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{wf.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Suggestions (frontend catalog) */}
      <Section icon={Boxes} title="Suggested actions" subtitle="Context suggestions by record type">
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(ALICE_SUGGESTION_CATALOG).map(([type, suggestions]) => (
            <Badge key={type} variant="outline" className="h-6 px-2 text-[11px]">
              {type}
              <span className="ml-1 text-muted-foreground">{suggestions?.length ?? 0}</span>
            </Badge>
          ))}
        </div>
      </Section>
    </div>
  );
}

function RecentAliceActivity() {
  const logs = useQuery({
    queryKey: ['ai-logs', 'recent'],
    queryFn: () => aiLogsApi.list(25),
    staleTime: 30_000,
  });
  // Alice chat turns only; quick-capture/recap live elsewhere.
  const rows = (logs.data?.items ?? []).filter((row) => row.action_type === 'chat');

  return (
    <Section
      icon={Activity}
      title="Recent Alice Activity"
      subtitle="Last 25 chat turns · audited via ai_logs · read-only"
    >
      {logs.isLoading ? (
        <div className="flex items-center gap-2 px-1 py-3 text-[12px] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading activity…
        </div>
      ) : rows.length === 0 ? (
        <p className="px-1 py-3 text-[12px] text-muted-foreground">
          No recent Alice chat activity{logs.error ? ' (or logging is unavailable)' : ''}.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          {rows.map((row, idx) => (
            <ActivityRow key={row.id} row={row} bordered={idx > 0} />
          ))}
        </div>
      )}
    </Section>
  );
}

function VaultToolCard({ tool }: { tool: AliceToolInfo }) {
  const displayName = tool.displayName ?? tool.name;
  return (
    <div className="rounded-lg border border-border px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[13px] font-medium text-foreground">{displayName}</span>
        <code className="font-mono text-[11px] text-muted-foreground">{tool.name}</code>
        <Badge variant={tool.access === 'write' ? 'warning' : 'muted'} className="h-5 px-1.5 text-[10px]">
          {tool.access}
        </Badge>
        <Badge variant={tool.requiresApproval ? 'outline' : 'success'} className="h-5 px-1.5 text-[10px]">
          {tool.requiresApproval ? 'requires approval' : 'auto-executes'}
        </Badge>
      </div>
      <p className="mt-1 text-[12px] leading-snug text-muted-foreground">{tool.description}</p>
      <div className="mt-2 grid gap-2 text-[11px] sm:grid-cols-3">
        <ToolFact label="Endpoint" value={tool.endpoint ?? 'n/a'} />
        <ToolFact label="Action" value={tool.gatewayAction ?? 'n/a'} />
        <ToolFact label="Approval" value={tool.approvalBehavior ?? (tool.requiresApproval ? 'Required' : 'None')} />
      </div>
      {tool.exampleInput && (
        <pre className="mt-2 max-h-36 overflow-auto whitespace-pre-wrap rounded-md bg-muted/50 px-2.5 py-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
          {JSON.stringify(tool.exampleInput, null, 2)}
        </pre>
      )}
    </div>
  );
}

function ToolFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background/60 px-2 py-1.5">
      <p className="text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">{label}</p>
      <p className="mt-0.5 break-words font-mono text-[11px] text-foreground">{value}</p>
    </div>
  );
}

function ActivityRow({ row, bordered }: { row: AiLogRow; bordered: boolean }) {
  const meta = (row.metadata ?? {}) as Record<string, unknown>;
  const workflowKey = typeof meta.workflowKey === 'string' ? meta.workflowKey : null;
  const toolsCalled = Array.isArray(meta.toolsCalled)
    ? (meta.toolsCalled as unknown[]).filter((t): t is string => typeof t === 'string')
    : [];
  const cost = typeof row.cost_cents === 'number' ? `$${(row.cost_cents / 100).toFixed(2)}` : null;
  const when = formatWhen(row.updated_at ?? row.created_at);

  return (
    <div className={cn('flex flex-col gap-1 px-3 py-2.5', bordered && 'border-t border-border')}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] tabular-nums text-muted-foreground sm:w-36 sm:flex-shrink-0">
          {when}
        </span>
        <Badge variant="muted" className="h-5 px-1.5 text-[10px]">{row.action_type}</Badge>
        <ActivityStatusBadge status={row.status} />
        {row.model && (
          <code className="font-mono text-[10px] text-muted-foreground">{row.model}</code>
        )}
        {workflowKey && (
          <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{workflowKey}</Badge>
        )}
        {cost && <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">{cost}</span>}
      </div>
      {toolsCalled.length > 0 && (
        <p className="text-[11px] text-muted-foreground/80">Tools: {toolsCalled.join(', ')}</p>
      )}
    </div>
  );
}

function ActivityStatusBadge({ status }: { status: AiLogRow['status'] }) {
  const variant: 'success' | 'warning' | 'muted' | 'outline' =
    status === 'success' || status === 'sent'
      ? 'success'
      : status === 'error'
        ? 'warning'
        : status === 'discarded'
          ? 'outline'
          : 'muted';
  return <Badge variant={variant} className="h-5 px-1.5 text-[10px]">{status}</Badge>;
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function EffectivePolicySection({ policy }: { policy: AliceEffectivePolicy }) {
  const list = (items: string[]) => (items.length ? items.join(', ') : 'none');
  return (
    <Section
      icon={ShieldCheck}
      title="Effective Policy"
      subtitle="Server-enforced · code-defined · read-only"
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <PolicyFact label="Portal" value={policy.portalKey} />
        <PolicyFact label="Role / actor" value={`${policy.role} · ${policy.actorType}`} />
        <PolicyFact label="Max workflow risk" value={String(policy.maxRiskLevel)} />
        <PolicyFact label="Approval required at risk" value={`≥ ${policy.approvalRequiredAtRiskLevel}`} />
        <PolicyFact label="Disabled connectors" value={list(policy.disabledConnectors)} />
        <PolicyFact label="Disabled tools" value={list(policy.disabledTools)} />
        <PolicyFact label="Disabled workflows" value={list(policy.disabledWorkflows)} />
        <PolicyFact
          label="Default model"
          value={policy.defaultModel ?? 'shared default'}
        />
      </div>
      {policy.notes && (
        <p className="mt-2 text-[11px] text-muted-foreground">{policy.notes}</p>
      )}
    </Section>
  );
}

function PolicyFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
        {label}
      </p>
      <p className="mt-0.5 break-words text-[12px] text-foreground">{value}</p>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <Card className="px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">{label}</p>
      <p className="mt-1 text-[22px] font-semibold leading-none tabular-nums text-foreground">{value}</p>
      {hint && <p className="mt-0.5 text-[10px] text-muted-foreground">{hint}</p>}
    </Card>
  );
}

function Section({
  icon: Icon,
  title,
  subtitle,
  children,
}: {
  icon: typeof Wrench;
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
        <h2 className="text-[15px] font-semibold">{title}</h2>
        {subtitle && <span className="text-[11px] text-muted-foreground">· {subtitle}</span>}
      </div>
      {children}
    </Card>
  );
}

function WorkflowStatusBadge({ status }: { status: AliceWorkflowStatus }) {
  const map: Record<AliceWorkflowStatus, { variant: 'success' | 'muted' | 'warning' | 'outline' | 'default' }> = {
    active: { variant: 'success' },
    testing: { variant: 'default' },
    draft: { variant: 'muted' },
    paused: { variant: 'warning' },
    retired: { variant: 'outline' },
  };
  return (
    <Badge variant={map[status].variant} className="h-5 px-1.5 text-[10px]">
      {status}
    </Badge>
  );
}

function ConnectionBadge({ status }: { status: AliceConnectionStatus }) {
  const map: Record<AliceConnectionStatus, { label: string; variant: 'success' | 'muted' | 'warning' | 'outline' }> = {
    configured: { label: 'Configured', variant: 'success' },
    available: { label: 'Available', variant: 'muted' },
    not_configured: { label: 'Not configured', variant: 'warning' },
    planned: { label: 'Planned', variant: 'outline' },
  };
  const { label, variant } = map[status];
  return <Badge variant={variant} className="h-6 flex-shrink-0 px-2 text-[11px]">{label}</Badge>;
}
